import { IconSidebarSimpleRightSquareFilled } from "@/assets/icons/central/IconSidebarSimpleRightSquareFilled";
import { withGitpodSize } from "@/assets/icons/utils";
import { IconMaximize } from "@/assets/icons/geist/IconMaximize";
import { IconMinimize } from "@/assets/icons/geist/IconMinimize";
import { IconPanelRightClose } from "@/assets/icons/geist/IconPanelRightClose";
import { type EnvironmentViewMode, ViewModeToggle } from "@/components/ai/ViewModeToggle";
import { OpenInEditorButton } from "@/components/environments/OpenInEditorButton";
import { Button } from "@/components/flexkit/Button";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import { cn } from "@/components/podkit/lib/cn";
import { Tooltip } from "@/components/Tooltip";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import { useFlagIsMobileVscodeEnabled } from "@/hooks/use-flag";
import type { PlainEnvironment } from "@/queries/environment-queries";
import { canOpen } from "@/routes/environments/phase";
import { ToggleDetailsPanelKeyBinding } from "@/utils/platform";
import { IconSidebarSimpleRightSquare } from "central-icons/IconSidebarSimpleRightSquare";
import { type FC, useMemo } from "react";

const SidebarIconClosed = withGitpodSize(IconSidebarSimpleRightSquare);

export const EnvironmentDetailsTopHeader: FC<{
    environment?: PlainEnvironment;
    viewMode: EnvironmentViewMode;
    onViewModeChange: (viewMode: EnvironmentViewMode) => void;
    onToggleDetailsPanel?: () => void;
    onToggleConversationPanel?: () => void;
    isConversationPanelOpen?: boolean;
    isVSCodeBrowserAllowed: boolean;
}> = ({
    environment,
    viewMode,
    onViewModeChange,
    onToggleDetailsPanel,
    onToggleConversationPanel,
    isConversationPanelOpen = true,
    isVSCodeBrowserAllowed,
}) => {
    const openable = useMemo(() => !!environment && canOpen(environment), [environment]);
    const { isMobileViewport: isMobile } = useIsMobileViewport();
    const { value: isMobileVscodeEnabled } = useFlagIsMobileVscodeEnabled();
    const canViewVSCodeBrowser = isVSCodeBrowserAllowed && openable;

    if (isMobile) {
        const hideCodeTabOnMobile = !isVSCodeBrowserAllowed || !isMobileVscodeEnabled;
        return (
            <div className="flex h-12 w-full items-center justify-between gap-2 border-b border-border-base px-4 py-2">
                <div className="flex h-6 min-w-10 shrink items-center gap-1">
                    <ViewModeToggle
                        value={viewMode}
                        onValueChange={onViewModeChange}
                        canViewVSCodeBrowser={canViewVSCodeBrowser}
                        hideCodeTab={hideCodeTabOnMobile}
                        data-testid="environment-view-mode-toggle-mobile"
                    />
                </div>
                <div className="flex items-center justify-end gap-1">
                    <Tooltip content="Show conversation">
                        <Button
                            variant="ghost"
                            className="rounded-[4px] border-0 p-0"
                            size="lg"
                            onClick={onToggleDetailsPanel}
                            LeadingIcon={IconPanelRightClose}
                            aria-label="Show conversation"
                            data-tracking-id="show-conversation-environment-details"
                        />
                    </Tooltip>
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-12 items-center gap-2 border-b border-border-subtle px-4 py-2 @container">
            <div className="flex flex-1 items-center gap-2">
                <ViewModeToggle
                    value={viewMode}
                    onValueChange={onViewModeChange}
                    canViewVSCodeBrowser={canViewVSCodeBrowser}
                    hideCodeTab={!isVSCodeBrowserAllowed}
                    data-testid="environment-view-mode-toggle"
                />
                {/* Show toggle button when Code tab is selected */}
                {onToggleConversationPanel && viewMode === "vscode" && (
                    <Tooltip usePortal content={isConversationPanelOpen ? "Expand" : "Show conversation"}>
                        <Button
                            variant="ghost"
                            className="rounded-[4px] border-0 p-2"
                            size="sm"
                            onClick={onToggleConversationPanel}
                            LeadingIcon={isConversationPanelOpen ? IconMaximize : IconMinimize}
                            aria-label={isConversationPanelOpen ? "Expand code view" : "Show conversation"}
                            data-tracking-id="toggle-conversation-panel-environment-details"
                        />
                    </Tooltip>
                )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
                {environment ? <OpenInEditorButton environment={environment} disabled={!openable} /> : null}

                <Tooltip
                    usePortal
                    content={
                        <div className="flex flex-row items-center gap-2">
                            <span>Hide details panel</span>
                            <KeyboardCombo keys={ToggleDetailsPanelKeyBinding} className="bg-surface-invert-tertiary" />
                        </div>
                    }
                >
                    <Button
                        variant="ghost"
                        className={cn("group relative rounded-[4px] border-0 p-2")}
                        size="sm"
                        onClick={onToggleDetailsPanel}
                        aria-label="Hide right panel"
                        data-tracking-id="toggle-details-panel-agent-execution-conversation"
                    >
                        <IconSidebarSimpleRightSquareFilled
                            size="sm"
                            className={cn(
                                "absolute transition-opacity duration-300",
                                "opacity-100 group-hover:opacity-0",
                            )}
                        />
                        <SidebarIconClosed
                            size="sm"
                            className={cn("transition-opacity duration-300", "opacity-0 group-hover:opacity-100")}
                        />
                    </Button>
                </Tooltip>
            </div>
        </div>
    );
};
